"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Users, ChevronDown, ChevronUp } from "lucide-react";
import Link from "next/link";
import CdnImage from "@/components/CdnImage";
import { trackRecommendation, setNavigationSource } from "@/lib/recommendation-tracker";
import { authFetch } from "@/lib/authFetch";

// ── Shared types (exported for use in EventClient) ──

export interface NetworkTrader {
  id?: string;
  address?: string;
  username: string;
  name?: string;
  image?: string | null;
  outcome?: string | null;
  side: string;
  sizeUsd: number;
  timestamp: string;
}

export interface MarketActivity {
  friends: { count: number; traders: NetworkTrader[] };
  watchlist: { count: number; traders: NetworkTrader[] };
  leaders: { count: number; traders: NetworkTrader[] };
  totalConnections: number;
  hasActivity: boolean;
}

export type TraderWithType = NetworkTrader & { type: "friend" | "leader" | "watchlist" };

// ── Floating tooltip for trader avatars (renders via portal to avoid overflow clipping) ──

function TraderTooltip({ trader, children }: { trader: NetworkTrader; children: React.ReactNode }) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [isClickOpen, setIsClickOpen] = useState(false);

  const show = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top });
  }, []);

  const hide = useCallback(() => {
    if (!isClickOpen) setPos(null);
  }, [isClickOpen]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isClickOpen) {
      setIsClickOpen(false);
      setPos(null);
    } else {
      setIsClickOpen(true);
      show();
    }
  }, [isClickOpen, show]);

  // Close on click outside
  useEffect(() => {
    if (!isClickOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsClickOpen(false);
        setPos(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isClickOpen]);

  return (
    <div ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onClick={handleClick} className="relative cursor-pointer">
      {children}
      {pos &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{ left: pos.x, top: pos.y, transform: "translate(-50%, -100%)" }}
          >
            <div className="mb-2 bg-[#1A1A1A] dark:bg-white rounded-[5px] px-3 py-2 shadow-xl border border-white/10 dark:border-gray-200 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-[5px] overflow-hidden flex-shrink-0 ${
                    !trader.image
                      ? "bg-gradient-to-br from-[#7C3AED] dark:from-[#CCFF00] to-[#5B21B6] dark:to-[#9ACD00] flex items-center justify-center"
                      : ""
                  }`}
                >
                  {trader.image ? (
                    <CdnImage src={trader.image} alt={trader.username} width={24} height={24} sizes="24px" className="w-full h-full object-cover" unoptimized />
                  ) : (
                    <span className="text-[8px] font-bold text-white dark:text-black">
                      {trader.username.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-[11px] font-inter-tight font-bold text-white dark:text-gray-900">
                    {trader.username}
                  </div>
                  <div className="text-[10px] font-inter-tight text-gray-400 dark:text-gray-500">
                    {trader.side === "BUY" ? "Bought" : "Sold"}{" "}
                    <span
                      className={
                        trader.outcome === "YES" || (!trader.outcome && trader.side === "BUY")
                          ? "text-[#22C55E]"
                          : "text-[#EF4444]"
                      }
                    >
                      {trader.outcome || (trader.side === "BUY" ? "YES" : "NO")}
                    </span>
                    {" · "}${trader.sizeUsd.toFixed(0)}
                  </div>
                </div>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-[#1A1A1A] dark:bg-white rotate-45 border-r border-b border-white/10 dark:border-gray-200" />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// ── Hook to fetch network activity ──

export function useNetworkActivity(marketSlugs: string[]) {
  const [activity, setActivity] = useState<Record<string, MarketActivity>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (marketSlugs.length === 0) {
      setLoading(false);
      return;
    }

    const fetchActivity = async () => {
      try {
        const slugs = marketSlugs.join(",");
        const res = await authFetch(`/api/social/markets/activity?slugs=${encodeURIComponent(slugs)}`);
        if (res.ok) {
          const data = await res.json();
          setActivity(data.activity || {});
        }
      } catch {
        // Silently fail
      }
      setLoading(false);
    };

    fetchActivity();
  }, [marketSlugs.join(",")]);

  return { activity, loading };
}

// ── Helper: get all traders for a market slug ──

export function getTradersForMarket(activity: Record<string, MarketActivity>, slug: string): TraderWithType[] {
  const marketActivity = activity[slug];
  if (!marketActivity?.hasActivity) return [];
  return [
    ...marketActivity.friends.traders.map((t) => ({ ...t, type: "friend" as const })),
    ...marketActivity.leaders.traders.map((t) => ({ ...t, type: "leader" as const })),
    ...marketActivity.watchlist.traders.map((t) => ({ ...t, type: "watchlist" as const })),
  ];
}

// ── Inline avatars component for market cards/rows ──

export function MarketNetworkAvatars({ traders }: { traders: TraderWithType[] }) {
  if (traders.length === 0) return null;

  return (
    <div className="flex items-center -space-x-1.5">
      {traders.slice(0, 3).map((trader, i) => (
        <TraderTooltip key={trader.username + i} trader={trader}>
          <div style={{ zIndex: 3 - i }} className="relative">
            <div
              className={`w-5 h-5 rounded-[5px] border-[1.5px] border-white dark:border-[#0A0A0A] overflow-hidden flex-shrink-0 ${
                !trader.image ? "bg-gradient-to-br from-[#7C3AED] dark:from-[#CCFF00] to-[#5B21B6] dark:to-[#9ACD00] flex items-center justify-center" : ""
              }`}
            >
              {trader.image ? (
                <CdnImage src={trader.image} alt={trader.username} width={20} height={20} sizes="20px" className="w-full h-full object-cover" unoptimized />
              ) : (
                <span className="text-[7px] font-bold text-white dark:text-black">
                  {trader.username.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </TraderTooltip>
      ))}
      {traders.length > 3 && (
        <div className="w-5 h-5 rounded-[5px] border-[1.5px] border-white dark:border-[#0A0A0A] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
          <span className="text-[7px] font-inter-tight font-bold text-gray-500">
            +{traders.length - 3}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Main NetworkBetsSection component ──

interface NetworkBetsSectionProps {
  markets: { slug: string; question: string; groupItemTitle?: string | null }[];
  activity: Record<string, MarketActivity>;
  loading?: boolean;
}

export default function NetworkBetsSection({ markets, activity, loading }: NetworkBetsSectionProps) {
  const [expanded, setExpanded] = useState(true);

  const hasData = Object.values(activity).some((a) => a.hasActivity);

  if (loading || !hasData) return null;

  const marketsWithActivity = markets
    .map((market) => {
      const traders = getTradersForMarket(activity, market.slug);
      if (traders.length === 0) return null;

      const yesBuyers = traders.filter((t) => t.outcome === "YES" || (!t.outcome && t.side === "BUY"));
      const noBuyers = traders.filter((t) => t.outcome === "NO" || (!t.outcome && t.side === "SELL"));

      return {
        slug: market.slug,
        title: market.groupItemTitle || market.question,
        yesBuyers,
        noBuyers,
        totalTraders: traders.length,
      };
    })
    .filter((x): x is { slug: string; title: string; yesBuyers: TraderWithType[]; noBuyers: TraderWithType[]; totalTraders: number } => x !== null);

  if (marketsWithActivity.length === 0) return null;

  const totalPeople = marketsWithActivity.reduce((sum, m) => sum + m.totalTraders, 0);

  return (
    <div className="mb-4 animate-fade-in">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-[#7C3AED]/5 dark:from-[#CCFF00]/5 to-transparent rounded-[5px] border border-[#7C3AED]/20 dark:border-[#CCFF00]/20 hover:border-[#7C3AED]/40 dark:hover:border-[#CCFF00]/40 transition-all"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#7C3AED] dark:text-[#CCFF00]" />
          <span className="text-sm font-satoshi font-bold text-gray-900 dark:text-white">
            Your Network&apos;s Bets
          </span>
          <span className="text-[10px] font-inter-tight text-gray-500 bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded-[5px]">
            {totalPeople} {totalPeople === 1 ? "person" : "people"}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {marketsWithActivity.map((market) => (
            <div
              key={market.slug}
              className="bg-white dark:bg-[#0F0F12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] p-3"
            >
              <Link href={`/polymarket/event/${(market as any).eventSlug || market.slug}`} prefetch={false} onClick={() => { trackRecommendation(market.slug, 'view_detail', 'feed'); setNavigationSource('feed'); }} className="block mb-2">
                <span className="text-xs font-satoshi font-bold text-gray-700 dark:text-gray-300 hover:text-[#7C3AED] dark:hover:text-[#CCFF00] transition-colors line-clamp-1">
                  {market.title}
                </span>
              </Link>
              <div className="space-y-2">
                {market.yesBuyers.length > 0 && (
                  <OutcomeRow outcome="YES" traders={market.yesBuyers} />
                )}
                {market.noBuyers.length > 0 && (
                  <OutcomeRow outcome="NO" traders={market.noBuyers} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OutcomeRow({ outcome, traders }: { outcome: "YES" | "NO"; traders: TraderWithType[] }) {
  const isYes = outcome === "YES";

  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-[10px] font-inter-tight font-black uppercase px-1.5 py-0.5 rounded ${
          isYes ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#EF4444]/15 text-[#EF4444]"
        }`}
      >
        {outcome}
      </span>

      <div className="flex items-center -space-x-2">
        {traders.slice(0, 5).map((trader, i) => (
          <TraderTooltip key={trader.username + i} trader={trader}>
            <div style={{ zIndex: 5 - i }} className="relative">
              <div
                className={`w-7 h-7 rounded-[5px] border-2 border-white dark:border-[#0A0A0A] overflow-hidden flex-shrink-0 ${
                  !trader.image ? "bg-gradient-to-br from-[#7C3AED] dark:from-[#CCFF00] to-[#5B21B6] dark:to-[#9ACD00] flex items-center justify-center" : ""
                }`}
              >
                {trader.image ? (
                  <CdnImage src={trader.image} alt={trader.username} width={28} height={28} sizes="28px" className="w-full h-full object-cover" unoptimized />
                ) : (
                  <span className="text-[10px] font-bold text-white dark:text-black">
                    {trader.username.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </TraderTooltip>
        ))}
        {traders.length > 5 && (
          <div className="w-7 h-7 rounded-[5px] border-2 border-white dark:border-[#0A0A0A] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-inter-tight font-bold text-gray-500">
              +{traders.length - 5}
            </span>
          </div>
        )}
      </div>

      <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 font-inter-tight truncate">
        <span className="truncate">
          {traders.slice(0, 3).map((t) => t.username).join(", ")}
        </span>
        {traders.length > 3 && (
          <span className="inline-flex items-center h-[18px] px-1.5 rounded-[5px] text-[10px] font-bold font-inter-tight bg-gray-100 dark:bg-[#1a1a1a] text-gray-500 dark:text-gray-400 flex-shrink-0 leading-none">
            +{traders.length - 3}
          </span>
        )}
      </span>
    </div>
  );
}
